import React, { useRef, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { connect } from 'react-redux';

import { toggleModalWithTimer, toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { setExtra } from 'apps/auth/actions/extraAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { DetailsItem } from 'apps/auth/components/common/DetailsItem/DetailsItem';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import ChangePersonalInfoPopup from 'apps/auth/components/profile/ChangePersonalInfoPopup';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import { BIRTHDAY, ITIN, NAME, NAME_F, SSN, EMAIL, ADDRESS, CITY, POST_CODE, RESIDENT_COUNTRY } from 'apps/auth/constants/fieldsName';
import { AUTH_EMAIL_CHECK_INBOX_PAGE, AUTH_EMAIL_CONFIRM_ACTION, AUTH_PROFILE } from 'apps/auth/constants/UrlConstants';
import { UNITED_ARAB_EMIRATES, USA } from 'apps/auth/constants/WellKnownCountries';
import { apiFetchData } from 'apps/auth/helpers/api';
import { getEmptyStringIfNull, isObject } from 'apps/auth/helpers/utils';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useCountryMobileMask } from 'apps/auth/hooks/useCountryMobileMask';

const ProfileFieldsPage = (props) => {
    const popupRef = useRef(null);
    const [showModalWithTimer, setShowModalWithTimer] = useState(false);
    const { canConfirmEmail, countryId, userCountryName } = useAuth();
    const {
        headerTitle,
        profileMobilePhone,
        profileSsnLabel,
        profileItinLabel,
        profileEmailLabel,
        needToConfirmEmailLabel,
        profileEmailConfirmButton,
        confirmingEmailStatus,
        profileResidenceAddressLabel,
        profileUsaResidenceAddressLabel,
        profileResidenceCountryName,
        successMessage,
    } = usePageMessages();
    const { profile_form: profileForm, needToShowConfirmEmailLabel, isEmailConfirmingStatus, userFlags, isCanChangeEmail } = useExtra();
    const { cards_and_accounts_allowed: hasGA, libre_allowed } = userFlags || {};
    const { defaultMask, masks, isReady, isMaskReadyForIso2 } = useCountryMobileMask(userCountryName);

    const noIdent = profileForm?.status !== 90;
    const currentAmlLimit = profileForm?.amlLimit?.currentLimit || 0;
    const isAML = profileForm?.limits?.aml;
    const canEditEmail = !hasGA && !libre_allowed && isCanChangeEmail;
    const canEditPhone = !hasGA && !libre_allowed && (noIdent || (isAML && currentAmlLimit === 0));
    const canEditPersonalDetails = !hasGA && !libre_allowed && profileForm?.editable;

    const notEmpty = (field) => {
        return isObject(field) && field.length > 0;
    };

    const canEditProfile = () => countryId !== USA && (canEditEmail || canEditPhone || canEditPersonalDetails);

    const confirmEmail = () => {
        props.toggleSpinner(true);
        apiFetchData(AUTH_EMAIL_CONFIRM_ACTION)
            .then((json) => {
                if (json) {
                    if (json.messageBodyMessages) {
                        props.updateMessages(json.messageBodyMessages);
                    }
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.setExtra(json.extra);
                        if (json.extra.checkInbox && json.messages?.successMessage) {
                            setShowModalWithTimer(true);
                        }
                    }
                }
                props.toggleSpinner(false);
            })
            .catch(() => {
                props.toggleSpinner(false);
            });
    };
    if (showModalWithTimer) {
        return (
            <ModalWithTimer
                text={successMessage}
                onClick={() => {
                    setShowModalWithTimer(false);
                    window.location.href = AUTH_EMAIL_CHECK_INBOX_PAGE;
                }}
            />
        );
    }

    if (profileForm) {
        return (
            <>
                <CommonPage
                    backUrl={AUTH_PROFILE}
                    hasBottomPanel={profileForm?.editable}
                    rightHeaderComponent={
                        canEditProfile() && (
                            <DiskButton
                                iconName="pencil"
                                type="button"
                                onClick={() => {
                                    popupRef.current?.open(
                                        <ChangePersonalInfoPopup
                                            popupRef={popupRef}
                                            canEditEmail={canEditEmail}
                                            canEditPhone={canEditPhone}
                                            canEditPersonalDetails={canEditPersonalDetails}
                                        />,
                                    );
                                }}
                            />
                        )
                    }
                >
                    <HeaderPanel title={headerTitle} />
                    <Row indent="adaptive-32-48">
                        <Row indent="adaptive-16-24">
                            <DiskBadge
                                src={profileForm?.avatar ? profileForm.avatar : null}
                                iconName={!profileForm?.avatar ? 'person-48' : null}
                                diskSize="large"
                                flagName={userCountryName}
                            />
                        </Row>
                        {(notEmpty(profileForm.toValues[NAME]) || notEmpty(profileForm.toValues[NAME_F])) && (
                            <Text variant="div" indent={'4px'}>
                                {`${getEmptyStringIfNull(profileForm.toValues[NAME])} ${getEmptyStringIfNull(profileForm.toValues[NAME_F])}`}
                            </Text>
                        )}
                        {notEmpty(profileForm.toValues[BIRTHDAY]) && <Text variant="div">{profileForm.toValues[BIRTHDAY]}</Text>}
                    </Row>
                    {notEmpty(profileForm.toValues[SSN]) && (
                        <Row indent="adaptive-32-48">
                            <DetailsItem detailsTitle={profileSsnLabel} detailsText={profileForm.toValues[SSN]} />
                        </Row>
                    )}
                    {notEmpty(profileForm.toValues[ITIN]) && (
                        <Row indent="adaptive-32-48">
                            <DetailsItem detailsTitle={profileItinLabel} detailsText={profileForm.toValues[ITIN]} />
                        </Row>
                    )}
                    <Row indent="adaptive-32-48">
                        <DetailsItem
                            detailsTitle={profileMobilePhone}
                            detailsText={
                                <>
                                    <Row flex justify="between">
                                        {isReady && isMaskReadyForIso2 && (
                                            <PhoneInput
                                                specialLabel={''}
                                                value={profileForm.phone}
                                                defaultMask={defaultMask}
                                                masks={masks}
                                                disableDropdown={true}
                                                inputProps={{ readOnly: true }}
                                                inputStyle={{
                                                    fontSize: 'inherit',
                                                    lineHeight: 'inherit',
                                                    fontFamily: 'inherit',
                                                    background: 'inherit',
                                                    color: 'inherit',
                                                    border: 'none',
                                                }}
                                            />
                                        )}
                                    </Row>
                                </>
                            }
                        />
                    </Row>
                    {notEmpty(profileForm.toValues[EMAIL]) && (
                        <Row indent="adaptive-32-48">
                            <DetailsItem
                                detailsTitle={needToShowConfirmEmailLabel ? needToConfirmEmailLabel : profileEmailLabel}
                                detailsText={
                                    <>
                                        <Row indent={isEmailConfirmingStatus || (canConfirmEmail && !isEmailConfirmingStatus) ? '12px' : null}>
                                            {profileForm.toValues[EMAIL]}
                                        </Row>
                                        {canConfirmEmail && !isEmailConfirmingStatus && (
                                            <Text variant="button" size="secondary" color="primary" onClick={confirmEmail}>
                                                {profileEmailConfirmButton}
                                            </Text>
                                        )}
                                        {isEmailConfirmingStatus && (
                                            <Row flex align="center">
                                                <Icon name="clock-filled" color="secondary" size="16px" rightIndent={'8px'} />
                                                <Text size="secondary" color="secondary">
                                                    {confirmingEmailStatus}
                                                </Text>
                                            </Row>
                                        )}
                                    </>
                                }
                                isAlert={needToShowConfirmEmailLabel}
                            />
                        </Row>
                    )}
                    {notEmpty(profileForm.toValues[ADDRESS]) && (
                        <DetailsItem
                            detailsTitle={USA === countryId ? profileUsaResidenceAddressLabel : profileResidenceAddressLabel}
                            detailsText={
                                <>
                                    <Text variant="div" indent={'16px'}>
                                        {profileForm.toValues[ADDRESS]}
                                    </Text>
                                    {notEmpty(profileForm.toValues[CITY]) && (
                                        <Text variant="div" indent={'16px'}>
                                            {profileForm.toValues[CITY]}
                                            {countryId !== UNITED_ARAB_EMIRATES && notEmpty(profileForm.toValues[POST_CODE])
                                                ? ` · ${profileForm.toValues[POST_CODE]}`
                                                : ''}
                                        </Text>
                                    )}
                                    {profileForm.toValues[RESIDENT_COUNTRY] !== '0' && <Text variant="div">{profileResidenceCountryName}</Text>}
                                </>
                            }
                        />
                    )}
                </CommonPage>
                <PopupNew ref={popupRef} />
            </>
        );
    }
    return false;
};

export default connect(null, { updateMessages, toggleSpinner, updatePageMessages, setExtra, toggleModalWithTimer })(BasePage(ProfileFieldsPage));
